import React, { useMemo, useState } from "react";
import { useClickOutside } from "@mantine/hooks";
import { SiteTheme, FilterData, FilterCategory } from "../../types/types";
import FilterGroup from "./FilterGroup";

interface RecipeFilterProps {
    filterData: FilterData;
    siteTheme: SiteTheme;
    searchPageUrl: string;
}

const RecipeFilter: React.FC<RecipeFilterProps> = ({
    filterData,
    siteTheme,
    searchPageUrl,
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [filterState, setFilterState] = useState(filterData);
    const ref = useClickOutside(() => setIsOpen(false));
    const baseUrl = window.location.origin + (window.location.pathname.indexOf(searchPageUrl) == -1 ? searchPageUrl : window.location.pathname);
    
    // Extract basic categories from initial filter data script tag
    const getBasicCategories = (): string => {
        try {
            const scriptTag = document.getElementById('recipe-filter-items');
            if (scriptTag) {
                const data = JSON.parse(scriptTag.textContent || '{}');
                return data.context?.basicCategories || '';
            }
        } catch (e) {
            console.warn('Failed to extract basic categories:', e);
        }
        return '';
    };

    const getFilterImageSource = () => {
        if (siteTheme === SiteTheme.GARANT)
            return "/static/images/garant/icons/filter-icon.svg";
        if (siteTheme === SiteTheme.ELDORADO)
            return "/static/images/eldorado/icons/filter-icon.svg";
    };

    const getCloseImageSource = () => {
        return "/static/images/base/cross-white-thin.svg";
    };

    const getUrlParams = (fromFilters : FilterCategory[]) : string => {
        const params = new URLSearchParams();
        fromFilters.forEach(group => {
            const selectedItems = group.items
                .filter(item => item.selected)
                .map(item => item.name);
            if (selectedItems.length > 0) {
                params.append(group.name, selectedItems.join(","));
            }
        });
        return params.toString();
    };

    const fetchFilters = (params?: string) => {
        const currentParams = new URLSearchParams(window.location.search);
        const apiParams = new URLSearchParams(params || '');
        
        // Add current text query (q) if it exists
        const textQuery = currentParams.get('q');
        if (textQuery) {
            apiParams.set('q', textQuery);
        }
        
        // Add basic categories from initial data
        const basicCategories = getBasicCategories();
        if (basicCategories) {
            apiParams.set('basicCategories', basicCategories);
        }
        
        const url = apiParams.toString() ? `/api/recipe-filter/filters?${apiParams.toString()}` : '/api/recipe-filter/filters';
        fetch(url)
            .then(res => res.json())
            .then((data: FilterData) => {
                setFilterState(data);
            })
            .catch(err => {
                console.error('Failed to fetch filters:', err);
            });
    };

    const toggleFilter = (groupName: string, filterName: string) => {
        const updatedFilters = filterState.categories.map(group =>
            group.name === groupName
                ? {
                    ...group,
                    items: group.items.map(item =>
                        item.name === filterName ? { ...item, selected: !item.selected } : item
                    ),
                }
                : group
        );

        setFilterState(prev => ({ ...prev, categories: updatedFilters }));
        fetchFilters(getUrlParams(updatedFilters));
    };

    const clearFilters = () => {
        const currentParams = new URLSearchParams(window.location.search);
        const filterParamNames = filterState.categories.map(cat => cat.name);
        
        // Check if any filter params exist
        const hasFilterParams = filterParamNames.some(name => currentParams.has(name));
        
        if (hasFilterParams) {
            const newUrl = window.location.origin + window.location.pathname;
            history.replaceState(null, "", newUrl);
            window.location.reload();
        } 
        else {
            setFilterState(prevState => ({
                ...prevState,
                categories: prevState.categories.map(group => ({
                    ...group,
                    items: group.items.map(item => {
                        return { ...item, selected: false, disabled: false }
                    })
                }))
            }));

            fetchFilters();
        }
    };

    const filterRecipes = () => {
        const currentParams = new URLSearchParams(window.location.search);
        const filterParamNames = filterState.categories.map(cat => cat.name);
        
        // Remove existing filter parameters
        filterParamNames.forEach(name => currentParams.delete(name));

        // Delete any paging parameter
        currentParams.delete('p');
        
        // Add new filter parameters
        const filterParams = new URLSearchParams(getUrlParams(filterState.categories));
        filterParams.forEach((value, key) => {
            currentParams.set(key, value);
        });
        
        const newUrl = baseUrl + (currentParams.toString() ? '?' + currentParams.toString() : '');
        location.href = newUrl;
    };

    const numActive = useMemo(() => {
        return filterState.categories.flatMap(g => g.items).filter(i => i.selected).length;
    }, [filterState.categories]);

    return (
        <div ref={ref}>
            <button
                aria-label={isOpen ? "Stäng filtermeny" : "Öppna filtermeny"}
                aria-expanded={isOpen}
                className="recipe-filter__title-bar"
                onClick={() => setIsOpen(!isOpen)}
            >
                <span className={`recipe-filter__title ${isOpen ? "recipe-filter__title--open" : ""}`}>Filtrera</span>
                {numActive > 0 && <span className="recipe-filter__num-active">{numActive} {numActive === 1 ? 'valt' : 'valda'}</span>}
                <img className="recipe-filter__icon" width="24" height="24" alt="" src={isOpen ? getCloseImageSource() : getFilterImageSource()} />
            </button>
            <div className={`recipe-filter__panel ${isOpen ? "recipe-filter__panel-open" : ""}`}>
                {filterState.categories.map((filterType, filterTypeIndex) => (
                    <FilterGroup
                        key={filterTypeIndex}
                        name={filterType.name}
                        title={filterType.title}
                        filters={filterType.items}
                        onToggleFilter={toggleFilter}
                    />
                ))}
                <div className="recipe-filter__buttons">
                    <button 
                        className="recipe-filter__button-clear" 
                        onClick={clearFilters}
                        disabled={numActive === 0}
                    >
                        Rensa
                    </button>
                    <button 
                        className="recipe-filter__button-filter" 
                        onClick={filterRecipes}
                    >
                        Visa träffar ({filterState.totalCount})
                    </button>
                </div>
            </div>
        </div>
    );
};

export default RecipeFilter;